import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { HandTitle } from "../../funnel/FunnelScreens";
import { itemVariants, Item, ContinueButton, CtaRow, Confetti, Loader, SubView, PANDA_POSTER } from "../NOKit";

// iOS NOMeetPixleyStep port: the character reveal — the funnel's framed hero
// with its colour ring, a hand-lettered title and confetti. While the avatar
// is still being drawn (it runs in the background from the photo step) the
// panda is at the easel instead.
const DRAWING_MESSAGES = [
  "Sketching their smile…",
  "Mixing the colours…",
  "Adding a spark of magic…",
  "Putting on the finishing touches…",
];

// Lets the step land before the frame pops
const REVEAL_DELAY_MS = 180;

// "default:<initial>" is the skip-photo sentinel, not a URL — fall back to
// the mascot in that case.
const isRealImage = (v) => v && !String(v).startsWith("default:");

const MeetStep = () => {
  const { username, profilePicture, originalProfileImage, nextStep, avatarPending } = useOnboarding();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (avatarPending) return undefined;
    const id = setTimeout(() => setRevealed(true), REVEAL_DELAY_MS);
    return () => clearTimeout(id);
  }, [avatarPending]);

  const name = username || "your hero";
  const image = [profilePicture, originalProfileImage].find(isRealImage) || null;

  return (
    <AnimatePresence mode="wait">
      {avatarPending ? (
        <Loader
          key="drawing"
          clip="panda_draw"
          title={<>Bringing <span className="fs-accent">{name}</span> to life…</>}
          messages={DRAWING_MESSAGES}
        />
      ) : (
        <SubView key="reveal">
          <Confetti />
          <Item className="fs-eyebrow">Meet your new friend</Item>
          <HandTitle lead="This is" hero={`${name}!`} itemVariants={itemVariants} />
          <motion.p variants={itemVariants} className="fs-sub">
            The hero of every cartoon and bedtime story, made just for them.
          </motion.p>

          <div className={`fs-hero${revealed ? " revealed" : ""}${image ? "" : " is-cutout"}`}>
            <span className="fs-hero-ring" aria-hidden="true" />
            {!image && <span className="fs-hero-stage" aria-hidden="true" />}
            <img
              src={image || PANDA_POSTER}
              alt={image ? name : ""}
              className="fs-hero-img"
              onError={(e) => {
                if (!image) e.target.src = "/assets/pixley-panda.png";
              }}
            />
          </div>

          <CtaRow>
            <ContinueButton onClick={nextStep} label={username ? `Create a story with ${username}` : "Create their first story"} />
          </CtaRow>
        </SubView>
      )}
    </AnimatePresence>
  );
};

export default MeetStep;
